import { SuretyAccountId, SuretyBondId, SuretyQuoteId, tagId } from "@inrev/common";
import {
	useAdminAttachTagToEntity,
	useAdminFetchAllEntityTags,
	useAdminFetchEntityTagsByEntity,
	useAdminRemoveTagFromEntity,
} from "@portal/api/entity/admin";
import { useOnClickOutside } from "@portal/hooks/useOnClickOutside";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils";
import { ErrorDisplay } from "../ui/ErrorDisplay";
import { MutationErrorFlag } from "../ui/MutationErrorFlag";
import { SearchableMultiselect } from "../ui/SearchableMultiselect";
import { Spinner } from "../ui/Spinner";
import { Tag } from "../ui/Tag";
import { TagActionButton } from "../ui/TagActionButton";

export type EntityTagsProps =
	| {
			entityType: "bond";
			entityId: SuretyBondId;
			accountId: SuretyAccountId;
	  }
	| {
			entityType: "quote";
			entityId: SuretyQuoteId;
			accountId: SuretyAccountId;
	  }
	| {
			entityType: "account";
			entityId: SuretyAccountId;
	  };

export const EntityTags: FC<EntityTagsProps> = (props) => {
	const { entityType, entityId } = props;
	const accountId = props.entityType === "account" ? undefined : props.accountId;
	const tagActionButtonRef = useRef<HTMLSpanElement>(null);
	const dropdownContainerRef = useRef<HTMLDivElement>(null);
	const [showTagSelector, setShowTagSelector] = useState(false);
	const { entityTags: allEntityTags } = useAdminFetchAllEntityTags();
	const {
		entityTags: entityTagsForEntity,
		entityTagsLoading,
		entityTagsError,
		refetchEntityTags,
	} = useAdminFetchEntityTagsByEntity({ entityType, entityId });
	const { attachTagToEntity, attachTagToEntityError, dismissAttachTagToEntityError } =
		useAdminAttachTagToEntity();
	const { removeTagFromEntity, removeTagFromEntityError, dismissRemoveTagFromEntityError } =
		useAdminRemoveTagFromEntity();

	const { entityTagOptions, selectedEntityTags } = useMemo(() => {
		const entityTagOptions = allEntityTags.map((tag) => ({
			label: tag.displayName,
			value: tag.id,
		}));

		const selectedEntityTags = entityTagsForEntity.map((tag) => tag.id);

		return { entityTagOptions, selectedEntityTags };
	}, [entityTagsForEntity, allEntityTags]);

	// Anchor the portaled dropdown to the manage button's viewport position so it
	// escapes ancestor overflow:hidden containers (e.g. the collapsible account header).
	const dropdownPosition = useMemo(() => {
		if (showTagSelector && tagActionButtonRef.current) {
			const rect = tagActionButtonRef.current.getBoundingClientRect();
			return { top: Math.floor(rect.bottom), left: Math.floor(rect.left) };
		}

		return { top: 0, left: 0 };
	}, [showTagSelector]);

	useOnClickOutside(dropdownContainerRef, (event) => {
		if (event.target instanceof Node && tagActionButtonRef.current?.contains(event.target)) return;
		setShowTagSelector(false);
	});

	// The dropdown is portaled with fixed positioning, so it can't follow the button when
	// the page scrolls — dismiss it instead. We also listen for wheel: the account header's
	// collapse gesture preventDefaults the first wheel, so no scroll event fires, but the
	// wheel still does. Events originating within the options list are ignored.
	useEffect(() => {
		if (!showTagSelector) return;
		const dismissOnOutsideScroll = (event: Event) => {
			if (event.target instanceof Node && dropdownContainerRef.current?.contains(event.target)) {
				return;
			}
			setShowTagSelector(false);
		};
		window.addEventListener("scroll", dismissOnOutsideScroll, true);
		window.addEventListener("wheel", dismissOnOutsideScroll, true);
		return () => {
			window.removeEventListener("scroll", dismissOnOutsideScroll, true);
			window.removeEventListener("wheel", dismissOnOutsideScroll, true);
		};
	}, [showTagSelector]);

	if (entityTagsLoading) {
		return (
			<div data-testid="entity-tags-loading" className="flex p-1 w-full items-center">
				<Spinner containerClassName="w-4 h-4" spinnerClassName="w-4 h-4" />
			</div>
		);
	}

	if (entityTagsError) {
		return (
			<ErrorDisplay
				error={entityTagsError}
				componentToDisplay={
					<div data-testid="error-loading-tags" className="flex w-full text-xs items-center">
						<span>Unable to load tags.</span>
						<a
							data-testid="retry-link"
							className="cursor-pointer m-1 text-link hover:text-link-hover"
							onClick={() => refetchEntityTags()}
						>
							Retry
						</a>
					</div>
				}
			></ErrorDisplay>
		);
	}

	return (
		<div
			data-testid="entity-tags"
			className={cn(
				"flex flex-wrap gap-2 relative items-center z-[50]",
				"max-w-[65vw] min-w-[50vw]",
			)}
		>
			{entityTagsForEntity.map((tag) => (
				<Tag key={tag.id}>{tag.displayName}</Tag>
			))}
			<span data-testid="manage-entity-tags-button" className="relative" ref={tagActionButtonRef}>
				<TagActionButton
					onClick={() => setShowTagSelector(!showTagSelector)}
					showManage={entityTagsForEntity.length > 0}
					iconOnly={entityTagsForEntity.length > 0}
				/>
			</span>
			{showTagSelector &&
				createPortal(
					<div
						ref={dropdownContainerRef}
						data-testid="entity-tags-dropdown"
						className="fixed z-[9999]"
						style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
						// The dropdown is portaled, but synthetic wheel events still bubble up the
						// React tree to the account header's collapse handler. Contain them so
						// scrolling the options doesn't collapse the header.
						onWheel={(e) => e.stopPropagation()}
					>
						<SearchableMultiselect
							options={entityTagOptions}
							selectedValues={selectedEntityTags}
							onOptionSelect={(selectedValue) => {
								const parsedTagId = tagId.parse(selectedValue);
								if (selectedEntityTags.includes(parsedTagId)) {
									removeTagFromEntity({ tagId: parsedTagId, entityType, entityId, accountId });
								} else {
									attachTagToEntity({ tagId: parsedTagId, entityType, entityId, accountId });
								}
							}}
						/>
					</div>,
					document.body,
				)}
			<MutationErrorFlag
				errorHeading="Attach Tag Error"
				mutationError={attachTagToEntityError}
				dismissError={dismissAttachTagToEntityError}
			/>
			<MutationErrorFlag
				errorHeading="Remove Tag Error"
				mutationError={removeTagFromEntityError}
				dismissError={dismissRemoveTagFromEntityError}
			/>
		</div>
	);
};
